import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Pencil, Trash2, Save, X, GripVertical, 
  HelpCircle, ChevronDown, ChevronUp, Loader2,
  User, FileText, MessageSquare, FolderOpen, CheckSquare, 
  Calendar, BookOpen, Bell, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navigation from '../components/Navigation';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Icon mapping
const iconMap = {
  User, FileText, MessageSquare, FolderOpen, CheckSquare, 
  Calendar, BookOpen, Bell, Shield, HelpCircle
};

const iconOptions = [
  { value: 'User', label: 'User' },
  { value: 'FileText', label: 'File/Document' },
  { value: 'MessageSquare', label: 'Message' },
  { value: 'FolderOpen', label: 'Folder' },
  { value: 'CheckSquare', label: 'Checkbox' },
  { value: 'Calendar', label: 'Calendar' },
  { value: 'BookOpen', label: 'Book' },
  { value: 'Bell', label: 'Bell' },
  { value: 'Shield', label: 'Shield' },
  { value: 'HelpCircle', label: 'Help' }
];

const colorOptions = [
  { value: 'bg-purple-100 text-purple-600', label: 'Purple' },
  { value: 'bg-blue-100 text-blue-600', label: 'Blue' },
  { value: 'bg-green-100 text-green-600', label: 'Green' },
  { value: 'bg-amber-100 text-amber-600', label: 'Amber' },
  { value: 'bg-red-100 text-red-600', label: 'Red' },
  { value: 'bg-indigo-100 text-indigo-600', label: 'Indigo' },
  { value: 'bg-teal-100 text-teal-600', label: 'Teal' },
  { value: 'bg-pink-100 text-pink-600', label: 'Pink' },
  { value: 'bg-slate-100 text-slate-600', label: 'Slate' },
  { value: 'bg-gray-100 text-gray-600', label: 'Gray' }
];

function AdminFAQ({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    icon: 'HelpCircle',
    color: 'bg-gray-100 text-gray-600',
    questions: []
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/admin/faqs`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch FAQs');
      const data = await response.json();
      setCategories(data);
      
      // If no categories exist, offer to seed defaults
      if (data.length === 0) {
        toast.info('No FAQs found. Click "Seed Default FAQs" to add starter content.');
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultFAQs = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/faqs/seed`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to seed FAQs');
      const data = await response.json();
      toast.success(data.message);
      fetchCategories();
    } catch (error) {
      console.error('Error seeding FAQs:', error);
      toast.error('Failed to seed FAQs');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/faqs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newCategory,
          order: categories.length + 1
        })
      });

      if (!response.ok) throw new Error('Failed to create category');
      
      toast.success('Category created successfully');
      setShowAddCategory(false);
      setNewCategory({
        name: '',
        icon: 'HelpCircle',
        color: 'bg-gray-100 text-gray-600',
        questions: []
      });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async (categoryId, updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/faqs/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update category');
      
      toast.success('Category updated successfully');
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ category? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/faqs/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete category');
      
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setSaving(false);
    }
  };

  const addQuestionToCategory = (categoryId) => {
    setCategories(prev => prev.map(cat => {
      if (cat.category_id === categoryId) {
        return {
          ...cat,
          questions: [...cat.questions, { question: '', answer: '' }]
        };
      }
      return cat;
    }));
  };

  const updateQuestion = (categoryId, questionIndex, field, value) => {
    setCategories(prev => prev.map(cat => {
      if (cat.category_id === categoryId) {
        const newQuestions = [...cat.questions];
        newQuestions[questionIndex] = {
          ...newQuestions[questionIndex],
          [field]: value
        };
        return { ...cat, questions: newQuestions };
      }
      return cat;
    }));
  };

  const removeQuestion = (categoryId, questionIndex) => {
    setCategories(prev => prev.map(cat => {
      if (cat.category_id === categoryId) {
        return {
          ...cat,
          questions: cat.questions.filter((_, idx) => idx !== questionIndex)
        };
      }
      return cat;
    }));
  };

  const saveQuestions = async (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    if (!category) return;

    // Validate questions
    const validQuestions = category.questions.filter(q => q.question.trim() && q.answer.trim());
    
    await handleUpdateCategory(categoryId, { questions: validQuestions });
  };

  const IconComponent = ({ iconName, className }) => {
    const Icon = iconMap[iconName] || HelpCircle;
    return <Icon className={className} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-[#80298F]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <HelpCircle className="h-7 w-7 text-[#80298F]" />
              Manage FAQs
            </h1>
            <p className="text-gray-600 mt-1">
              Add, edit, or remove frequently asked questions
            </p>
          </div>
          <div className="flex gap-2">
            {categories.length === 0 && (
              <Button
                onClick={seedDefaultFAQs}
                variant="outline"
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Seed Default FAQs
              </Button>
            )}
            <Button
              onClick={() => setShowAddCategory(true)}
              className="bg-[#80298F] hover:bg-[#6B2278]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>

        {/* Add Category Form */}
        {showAddCategory && (
          <Card className="mb-6 border-2 border-dashed border-[#80298F]">
            <CardHeader>
              <CardTitle className="text-lg">New FAQ Category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Category Name</Label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Getting Started"
                  />
                </div>
                <div>
                  <Label>Icon</Label>
                  <Select
                    value={newCategory.icon}
                    onValueChange={(value) => setNewCategory(prev => ({ ...prev, icon: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <IconComponent iconName={opt.value} className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Color</Label>
                  <Select
                    value={newCategory.color}
                    onValueChange={(value) => setNewCategory(prev => ({ ...prev, color: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${opt.value.split(' ')[0]}`}></div>
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddCategory(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCategory}
                  disabled={saving}
                  className="bg-[#80298F] hover:bg-[#6B2278]"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Category
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ Categories */}
        {categories.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQ Categories</h3>
              <p className="text-gray-500 mb-4">Get started by adding a category or seeding default FAQs.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <Card key={category.category_id} className="overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleCategory(category.category_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${category.color}`}>
                      <IconComponent iconName={category.icon} className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{category.name}</h3>
                      <p className="text-sm text-gray-500">{category.questions.length} questions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCategory(editingCategory === category.category_id ? null : category.category_id);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category.category_id);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {expandedCategories[category.category_id] ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Edit Category Settings */}
                {editingCategory === category.category_id && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                      <div>
                        <Label>Category Name</Label>
                        <Input
                          value={category.name}
                          onChange={(e) => {
                            setCategories(prev => prev.map(c => 
                              c.category_id === category.category_id 
                                ? { ...c, name: e.target.value }
                                : c
                            ));
                          }}
                        />
                      </div>
                      <div>
                        <Label>Icon</Label>
                        <Select
                          value={category.icon}
                          onValueChange={(value) => {
                            setCategories(prev => prev.map(c => 
                              c.category_id === category.category_id 
                                ? { ...c, icon: value }
                                : c
                            ));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {iconOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <div className="flex items-center gap-2">
                                  <IconComponent iconName={opt.value} className="h-4 w-4" />
                                  {opt.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Color</Label>
                        <Select
                          value={category.color}
                          onValueChange={(value) => {
                            setCategories(prev => prev.map(c => 
                              c.category_id === category.category_id 
                                ? { ...c, color: value }
                                : c
                            ));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {colorOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-4 h-4 rounded ${opt.value.split(' ')[0]}`}></div>
                                  {opt.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <Button
                        onClick={() => handleUpdateCategory(category.category_id, {
                          name: category.name,
                          icon: category.icon,
                          color: category.color
                        })}
                        disabled={saving}
                        size="sm"
                        className="bg-[#80298F] hover:bg-[#6B2278]"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Settings
                      </Button>
                    </div>
                  </div>
                )}

                {/* Questions List */}
                {expandedCategories[category.category_id] && (
                  <CardContent className="border-t pt-4">
                    <div className="space-y-4">
                      {category.questions.map((q, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-3">
                              <div>
                                <Label className="text-xs text-gray-500">Question</Label>
                                <Input
                                  value={q.question}
                                  onChange={(e) => updateQuestion(category.category_id, idx, 'question', e.target.value)}
                                  placeholder="Enter question..."
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Answer</Label>
                                <Textarea
                                  value={q.answer}
                                  onChange={(e) => updateQuestion(category.category_id, idx, 'answer', e.target.value)}
                                  placeholder="Enter answer..."
                                  rows={3}
                                />
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeQuestion(category.category_id, idx)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}

                      <div className="flex items-center justify-between pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addQuestionToCategory(category.category_id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Question
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveQuestions(category.category_id)}
                          disabled={saving}
                          className="bg-[#80298F] hover:bg-[#6B2278]"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Questions
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminFAQ;
